import { useQueries } from '@tanstack/react-query'
import { BriefcaseBusiness } from 'lucide-react'
import { useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import {
  fetchBrokersEmployers,
  useGetBrokersEmployers,
  useGetBrokersEmployersQueryKey,
} from 'components/hooks/useGetBrokersEmployers'

import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from '../../reusable/table/ComboboxMultiSelectFilter'
import { type MultipleSelectedValuesFilter } from '../types'
import FilterManagementDropdown from './FilterManagementDropdown'

const pagination = { pageIndex: 1, pageSize: 10 }

function createNameFilter (name: string) {
  return [{ id: 'name', value: name }]
}

// Helper function to create filters with review statuses (e.g., to include pending/denied employers)
function createFiltersWithReviewStatuses (name: string, reviewStatuses?: string[]) {
  const filters: Array<{ id: string; value: string | string[] }> = [
    { id: 'name', value: name },
  ]

  if (reviewStatuses && reviewStatuses.length > 0) {
    filters.push({ id: 'review_statuses', value: reviewStatuses })
  }

  return filters
}

const EmployerFilter = (props: MultipleSelectedValuesFilter) => {
  const [query, setQuery] = useState('')
  // Debounce the query to a request is only sent after the user stops typing for 300ms.
  const [queryDebounced] = useDebounceValue(query, 300)

  const { data, isLoading } = useGetBrokersEmployers({
    pagination,
    filters: createNameFilter(queryDebounced),
  })
  const employers = data?.data || []

  const employerOptions: ComboboxFilterOption<string>[] =
    employers?.map(({ id, name }) => {
      return {
        label: name,
        value: id,
      }
    }) || []

  // If the selected employers are not in the results, fetch
  // the missing employers.
  const employersMissingFromResults = props.selectedValues.filter(
    (id) => !employerOptions.some(({ value }) => value === id)
  )

  const missingEmployerResults = useQueries({
    queries: employersMissingFromResults.map((id) => {
      // The default query only includes approved employers. Use the optional filters to include pending/denied employers.
      const filters = createFiltersWithReviewStatuses(id, ['approved', 'pending', 'denied'])
      return {
        queryKey: useGetBrokersEmployersQueryKey({
          pagination,
          filters,
        }),
        queryFn: () =>
          fetchBrokersEmployers({
            pagination,
            filters,
          }),
      }
    }),
  })

  // Merge the results so that the selected employers are included in the options.
  const employerOptionsIncludingSelectedEmployers = [
    ...employerOptions,
    ...(missingEmployerResults
      ?.map(({ data }) => data?.data[0])
      .map((employer) => ({
        label: employer?.name || '',
        value: employer?.id || '',
      })) || []),
  ]

  return (
    <div>
      <ComboboxMultiSelectFilter
        {...props}
        options={[
          {
            groupLabel: 'Employers',
            options: employerOptionsIncludingSelectedEmployers,
          },
        ]}
        placeholder='Search Employers to filter...'
        onValueChange={setQuery}
        isLoading={isLoading}
      >
        <FilterManagementDropdown
          {...props}
          options={employerOptionsIncludingSelectedEmployers}
          noSelectionText='Employer'
          pluralIdentifier='Employers'
        >
          <BriefcaseBusiness size={20} />
        </FilterManagementDropdown>
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default EmployerFilter
